import { ContactCreditorCollection } from '/@/modules/contact/models/ContactCreditorCollection';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { LiabilityMeta } from '/@/modules/liability/models/meta/LiabilityMeta';
import { mix } from 'mixwith/mixwith';
import { LiabilityFormMixin } from '/@/modules/liability/models/mixins/LiabilityFormMixin';
import NumeralService from '/@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '/@/modules/core/app/constants/data.constants';
import { LoanAndCreditPaymentAccountType } from '/@/modules/liability/enums/LoanAndCreditPaymentAccountType';
import { FuneralAndEstateExpenseType } from '/@/modules/liability/enums/FuneralAndEstateExpenseType';
import { UnpaidTaxType } from '/@/modules/liability/enums/UnpaidTaxType';
import { PersonalExpenseType } from '/@/modules/liability/enums/PersonalExpenseType';
import { LiabilityType } from '/@/modules/liability/enums/LiabilityType';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { LiabilityStatus } from '/@/modules/liability/enums/LiabilityStatus';
import Lang from '/@/modules/core/app/services/Lang';
import { LiabilityShareOwnershipType } from '/@/modules/liability/enums/LiabilityShareOwnershipType';
import { AccountingOwnershipFormMixin } from '/@/modules/accounting/components/mixins/AccountingOwnershipFormMixin';
import { reduceJoin } from '/@/modules/core/app/helpers/StringHelper';
import { LiabilityUsageType } from '/@/modules/liability/enums/LiabilityUsageType';
import { CompensationType } from '@/modules/liability/enums/CompensationType';
import { isNil } from 'lodash';

export class Liability extends mix(BaseEntityModel).with(
  LiabilityFormMixin,
  AccountingOwnershipFormMixin
) {
  /**
   * @var {number}
   */
  id;

  /**
   * @var {number}
   */
  modelId;

  /**
   * @var {string}
   */
  modelType;

  /**
   * @var {number}
   */
  estateId;

  /**
   * @var {?number}
   */
  assetId;

  /**
   * @var {?number}
   */
  accruedInterestsSum;

  /**
   * @var {string}
   */
  name;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {string}
   */
  status;

  /**
   * @var {string}
   */
  isCompleted;

  /**
   * @var {boolean}
   */
  isAccountable;

  /**
   * @var {number}
   */
  currentBalanceAmount;

  /**
   * @var {number}
   */
  estimatedAmount;

  /**
   * @var {number}
   */
  amountPaid;

  /**
   * @var {number}
   */
  remainingBalanceAmount;

  /**
   * @var {number}
   */
  reimbursementAmount;

  /**
   * @var {number}
   */
  interestRate;

  /**
   * @var {number}
   */
  atDeathAmount;

  /**
   * @var {number}
   */
  estateWorth;

  /**
   * @var {Currency}
   */
  currency;

  /**
   * @var {number}
   */
  currencyId;

  /**
   * @var {object}
   */
  meta;

  /**
   * @var {number}
   */
  countyId;

  /**
   * @var {number}
   */
  jurisdictionId;

  /**
   * @var {number}
   */
  displayAmount;

  /**
   * @var {number}
   */
  countryId;

  /**
   * @var {boolean}
   */
  isLocatedOutsideJurisdiction;

  /**
   * @var {number}
   */
  createdByUserId;

  /**
   * @var {boolean}
   */
  isProbatable;

  /**
   * @var {boolean}
   */
  isProbateConfirmed;

  /**
   * @var {string}
   */
  dateAcquired;

  /**
   * @var {number}
   */
  valueAtMarriage;

  /**
   * @var {number}
   */
  valueToTheEstate;

  /**
   * @var {string}
   */
  usage;

  /**
   * @var {boolean}
   */
  isLocked;

  /**
   * @relationship
   * @var {AccruedInterest[]}
   */
  accruedInterests;

  /**
   * @relationship
   * @var {DocumentCollection|Document[]}
   */
  documents;

  /**
   * @relationship
   * @var {Contact[]}
   */
  contacts;

  /**
   * @relationship
   * @var {EstateAccountForm[]}
   */
  estateAccounts;

  /**
   * @relationship
   * @var {ContactCreditorCollection}
   */
  creditors;

  /**
   * @relationship
   * @var {FinancialAccount}
   */
  financialAccount;

  /**
   * @relationship
   * @var {FinancialAccountTransaction[]}
   */
  financialAccountTransactions;

  /**
   * @relationship
   * @var {Address}
   */
  address;

  /**
   * @relationship
   * @var {Audit[]}
   */
  audits;

  /**
   * @relationship
   * @var {?Asset}
   */
  asset;

  /**
   * @relationship
   * @var {Note[]}
   */
  notes;

  /**
   * @relationship
   * @var {LiabilityCompensationItem[]}
   */
  liabilityCompensationItems;

  /**
   * @relationship
   * @var {EstateTaxFiling}
   */
  estateTaxFiling;

  /**
   * @relationship
   * @var {EstateAccountingQuebecDistribution}
   */
  estateAccountingQuebecDistribution;

  /**
   * @var {LiabilityEstateFormCollection}
   */
  estateForms;

  /**
   * @relationship
   * @var {ReferralPrefillingInventory}
   */
  referralPrefillingInventory;

  /**
   * @relationship
   * @var {ReferralPrefillingInventory}
   */
  partnerReferralPrefillingInventory;

  constructor(model = {}) {
    super(model);
    super.setRelationships(this, model, this.getModelName());

    this.id = model.id;
    this.modelId = model.modelId;
    this.modelType = model.modelType;
    this.estateId = model.estateId;
    this.assetId = model.assetId;
    this.accruedInterestsSum = model.accruedInterestsSum;
    this.name = model.name;
    this.type = model.type;
    this.status = model.status;
    this.isCompleted = model.isCompleted;
    this.isAccountable = model.isAccountable;
    this.displayAmount = model.displayAmount;
    this.currentBalanceAmount = model.currentBalanceAmount;
    this.estateWorth = model.estateWorth;
    this.estimatedAmount = model.estimatedAmount;
    this.reimbursementAmount = model.reimbursementAmount;
    this.interestRate = model.interestRate;
    this.atDeathAmount = model.atDeathAmount;
    this.amountPaid = model.amountPaid;
    this.remainingBalanceAmount = model.remainingBalanceAmount;
    this.currencyId = model.currencyId || null;
    this.meta = LiabilityMeta.factory(this.type, model.meta);
    this.countyId = model.countyId;
    this.jurisdictionId = model.jurisdictionId;
    this.countryId = model.countryId;
    this.isLocatedOutsideJurisdiction = model.isLocatedOutsideJurisdiction;
    this.createdByUserId = model.createdByUserId;
    this.isProbatable = model.isProbatable;
    this.isProbateConfirmed = model.isProbateConfirmed;
    this.dateAcquired = model.dateAcquired;
    this.valueAtMarriage = model.valueAtMarriage;
    this.valueToTheEstate = model.valueToTheEstate;
    this.usage = model.usage;
    this.isLocked = model.isLocked;
    this.creditors = ContactCreditorCollection.mapModel(model.creditors);
  }

  /**
   *
   * @returns {string}
   */
  getModelName() {
    return ModelType.LIABILITY;
  }

  /**
   * @returns {number}
   */
  get balanceDueByTheEstate() {
    if (!isNil(this.remainingBalanceAmount)) {
      return (
        (this.remainingBalanceAmount + (this.accruedInterestsSum ?? 0)) *
        ((this.computeEstateShareForOwnershipAssetOrLiability ?? 100) / 100)
      );
    }

    return (
      this.currentBalanceAmount *
      ((this.computeEstateShareForOwnershipAssetOrLiability ?? 100) / 100)
    );
  }

  /**
   * @returns {string}
   */
  get formattedDisplayValue() {
    return NumeralService.formatValue(
      this.displayAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedDisplayValueWithCurrency() {
    return NumeralService.formatValue(
      this.displayAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedCurrentBalanceAmount() {
    return NumeralService.formatValue(
      this.currentBalanceAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedAccruedInterestsSum() {
    return NumeralService.formatValue(
      this.accruedInterestsSum,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      ''
    );
  }

  /**
   * @returns {string}
   */
  get formattedCurrentBalanceAmountWithCurrency() {
    return NumeralService.formatValue(
      this.currentBalanceAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedBalanceDueByTheEstateWithCurrency() {
    return NumeralService.formatValue(
      this.balanceDueByTheEstate,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedBalanceRemainingWithCurrency() {
    return NumeralService.formatValue(
      this.balanceDueByTheEstate - this.amountPaid,
      ColumnFormat.FORMAT_CURRENCY,
      2,
      false,
      '$'
    );
  }

  /**
   * @returns {string}
   */
  get formattedAmountPaidWithCurrency() {
    return NumeralService.formatValue(this.amountPaid, ColumnFormat.FORMAT_CURRENCY, 2, false, '$');
  }

  /**
   * @returns {string}
   */
  get formattedAmountPaid() {
    return NumeralService.formatValue(this.amountPaid, ColumnFormat.FORMAT_CURRENCY, 2, false, '');
  }

  /**
   * @returns {string}
   */
  get estateSharePercentage() {
    return NumeralService.formatValue(
      this.computeEstateShareForOwnershipAssetOrLiability,
      ColumnFormat.FORMAT_PERCENT,
      2
    );
  }

  /**
   * @returns {boolean}
   */
  get hasShareOfResponsibilityType() {
    return !!this.meta.shareOwnershipType;
  }

  /**
   * @returns {LiabilityShareOwnershipType|undefined}
   */
  get shareOwnershipType() {
    return LiabilityShareOwnershipType.getByKey(this.meta?.shareOwnershipType);
  }

  /**
   * @returns {string}
   */
  get accountTypeValue() {
    return this.financialAccount?.subType
      ? LoanAndCreditPaymentAccountType.getByKey(this.financialAccount.subType).value
      : '';
  }

  /**
   * @returns {string}
   */
  get funeralAndEstateExpenseTypeValue() {
    return FuneralAndEstateExpenseType.getByKey(this.meta.type).value;
  }

  /**
   * @returns {string}
   */
  get unpaidTaxTypeValue() {
    return UnpaidTaxType.getByKey(this.meta.type).value;
  }

  /**
   * @returns {string}
   */
  get personalExpenseTypeValue() {
    return PersonalExpenseType.getByKey(this.meta.type)?.value;
  }

  /**
   * @returns {LiabilityType}
   */
  get typeEnum() {
    return LiabilityType.getByKey(this.type);
  }

  /**
   * @returns {string}
   */
  get displayType() {
    return LiabilityType.getByKey(this.type).value;
  }

  /**
   * @returns {string}
   */
  get displayModelName() {
    return Lang.t('accounting.liability-lowercase');
  }

  /**
   * @returns {string}
   */
  get displayModelNameWithDefiniteArticle() {
    return Lang.t('accounting.the-liability-lowercase');
  }

  /**
   * @returns {Boolean}
   */
  get isExecutorCompensationType() {
    return this.type === LiabilityType.EXECUTOR_COMPENSATION.key;
  }

  /**
   * @returns {Boolean}
   */
  get isExecutorDisbursementType() {
    return this.type === LiabilityType.EXECUTOR_DISBURSEMENT.key;
  }

  /**
   * @returns {Boolean}
   */
  get isLoanAndCreditType() {
    return this.type === LiabilityType.LOAN_AND_CREDIT_PAYMENT.key;
  }

  /**
   * @returns {Boolean}
   */
  get isUnpaidTax() {
    return this.type === LiabilityType.UNPAID_TAX.key;
  }

  /**
   * @returns {Boolean}
   */
  get isPotentialSpouseClaim() {
    return this.type === LiabilityType.POTENTIAL_SPOUSE_CLAIM.key;
  }

  /**
   * @returns {LiabilityStatus}
   */
  get statusEnum() {
    return LiabilityStatus.getByKey(this.status);
  }

  /**
   * @returns {boolean}
   */
  get isPaid() {
    return this.status === LiabilityStatus.PAID.key;
  }

  /**
   * @returns {boolean}
   */
  get isReconciled() {
    return this.status === LiabilityStatus.RECONCILED.key;
  }

  /**
   * @returns {boolean}
   */
  get isToBePaid() {
    return this.status === LiabilityStatus.TO_BE_PAID.key;
  }

  /**
   * @returns {boolean}
   */
  get isToBePaidLoan() {
    return this.isLoanAndCreditType && this.isToBePaid;
  }

  /**
   * @returns {boolean}
   */
  get isPaidLoan() {
    return this.isLoanAndCreditType && this.isPaid;
  }

  /**
   * @returns {boolean}
   */
  get isPersonalExpenseRentType() {
    return this.meta.type === PersonalExpenseType.RENT.key;
  }

  /**
   * @returns {boolean}
   */
  get isFamilyUsage() {
    return this.usage === LiabilityUsageType.FAMILY.key;
  }

  /**
   * @returns {boolean}
   */
  get isWorkUsage() {
    return this.usage === LiabilityUsageType.WORK.key;
  }

  /**
   * @returns {boolean}
   */
  get isPersonalUsage() {
    return this.usage === LiabilityUsageType.PERSONAL.key;
  }

  /**
   * @returns {boolean}
   */
  get isPrivatePropertyPersonalExpense() {
    return this.isWorkUsage || this.isPersonalUsage;
  }

  /**
   * @returns {boolean}
   */
  get isAssetLinkedToMatrimonialRegime() {
    return this.asset && this.asset?.estateAccountingQuebecDistribution?.isMatrimonialRegime;
  }

  /**
   * @returns {boolean}
   */
  get isAssetLinkedToFamilyPatrimony() {
    return this.asset && this.asset?.estateAccountingQuebecDistribution?.isFamilyPatrimony;
  }

  /**
   * @returns {string}
   */
  get displayOwnershipType() {
    return this.hasShareOfResponsibilityType
      ? `${LiabilityShareOwnershipType.getByKey(this.meta.shareOwnershipType).value} ${Lang.t(
          'liability.1a20929f-e63e-49bf-bd36-b6f949b917aa'
        )}`
      : '';
  }

  /**
   * @returns {string}
   */
  get title() {
    switch (this.type) {
      case LiabilityType.LOAN_AND_CREDIT_PAYMENT.key:
        return this.formatName(this.financialAccount.name, this.accountTypeValue);
      case LiabilityType.EXECUTOR_DISBURSEMENT.key:
        return this.formatName(this.estateAccounts[0].user.name, this.displayType);
      case LiabilityType.UNPAID_TAX.key:
        return this.formatName(this.unpaidTaxTypeValue, this.meta.taxYear);
      case LiabilityType.PERSONAL_EXPENSE.key:
        return this.formatName(this.personalExpenseTypeValue, this.meta.nameOfIssuer);
      case LiabilityType.ESTATE_EXPENSE.key:
        return this.formatName(this.funeralAndEstateExpenseTypeValue, this.meta.nameOfIssuer);
      case LiabilityType.OTHER_CREDITOR.key:
        return `${this.meta.nameOfIssuer} ${Lang.t('liability.expense')}`;
      case LiabilityType.ESTIMATED_FINAL_TAXES.key:
        return Lang.t('liability.estimated-final-taxes');
      default:
        return this.meta.title;
    }
  }

  /**
   * @param {string} newStatus
   */
  setStatus(newStatus) {
    this.status = newStatus;
  }

  /**
   * @param {number} newAmountPaid
   */
  setAmountPaid(newAmountPaid) {
    this.amountPaid = newAmountPaid;
  }

  /**
   * @param args
   * @returns {string}
   */
  formatName(...args) {
    return reduceJoin(args, ' - ');
  }

  /**
   * @returns {Document|*}
   */
  getUnpaidTaxDocument() {
    if (this.isLocked && this.estateTaxFiling.documents.noticeOfAssessmentDocument.createdAt) {
      return this.estateTaxFiling.documents.noticeOfAssessmentDocument;
    }

    if (this.isLocked) {
      return null;
    }

    return this.documents.liabilityTaxDocument;
  }

  get formattedName() {
    if (LiabilityType.LOAN_AND_CREDIT_PAYMENT.key === this.type && this.financialAccount?.number) {
      return `${this.title} ( ${this.financialAccount.maskedAccountNumberLastFourDigits})`;
    }

    return this.name;
  }

  /**
   * @returns {?string}
   */
  get compensationDisplayValue() {
    return CompensationType.getByKey(this.meta.type)?.value;
  }

  /**
   * @returns {User[]}
   * For Cashflows V2
   */
  get coOwnerUsers() {
    return this.estateAccounts?.map((account) => account.user).filter((user) => !!user) ?? [];
  }

  /**
   * @returns {boolean}
   */
  get hasReferralPrefillingInventory() {
    return !!this.referralPrefillingInventory || !!this.partnerReferralPrefillingInventory;
  }
}
